/**
 * Sarden Modal Utilities
 * Generic modal factory — open, close, reset, escape, click-outside.
 * Used by collection page modals. Other pages can migrate to this over time.
 *
 * Usage:
 *   SardenModal.init({
 *     modalId:        'collEnquiryModal',
 *     formId:         'collEnquiryForm',
 *     errorSummaryId: 'collEnquiryErrorSummary',
 *     openFnName:     'openCollEnquiryModal',
 *     closeFnName:    'closeCollEnquiryModal',
 *     submitLabel:    'Submit Enquiry'   // optional, defaults to 'Submit Enquiry'
 *   });
 */

window.SardenModal = (function () {
    'use strict';

    var SUBMIT_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>';

    /**
     * Register a modal instance.
     * Creates window.openFnName and window.closeFnName global functions,
     * and wires up Escape key + click-outside dismissal.
     *
     * @param {Object} config
     * @param {string} config.modalId         - id of the .modal-overlay element
     * @param {string} config.formId          - id of the <form> element inside the modal
     * @param {string} config.errorSummaryId  - id of the .error-summary element
     * @param {string} config.openFnName      - name to register on window for open
     * @param {string} config.closeFnName     - name to register on window for close
     * @param {string} [config.submitLabel]   - button label text (default: 'Submit Enquiry')
     */
    function init(config) {
        var modalId        = config.modalId;
        var formId         = config.formId;
        var errorSummaryId = config.errorSummaryId;
        var openFnName     = config.openFnName;
        var closeFnName    = config.closeFnName;
        var submitLabel    = config.submitLabel || 'Submit Enquiry';

        /* ── open ── */
        window[openFnName] = function () {
            var modal        = document.getElementById(modalId);
            var form         = document.getElementById(formId);
            var errorSummary = document.getElementById(errorSummaryId);

            if (modal) {
                modal.classList.add('active');
                document.body.style.overflow = 'hidden';
            }
            if (form && window.SardenFormValidator) {
                SardenFormValidator.clearErrors(form, errorSummary);
                var btn = form.querySelector('.modal-submit');
                if (btn) {
                    btn.innerHTML    = SUBMIT_SVG + ' ' + submitLabel;
                    btn.style.background  = '';
                    btn.style.boxShadow   = '';
                    btn.disabled     = false;
                }
            }
        };

        /* ── close ── */
        window[closeFnName] = function () {
            var modal = document.getElementById(modalId);
            if (modal) {
                modal.classList.remove('active');
                document.body.style.overflow = '';
            }
        };

        /* ── escape key ── */
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') {
                window[closeFnName]();
            }
        });

        /* ── click outside modal content ── */
        document.addEventListener('click', function (e) {
            if (e.target && e.target.id === modalId) {
                window[closeFnName]();
            }
        });
    }

    /**
     * Handle Shopify's post-submit redirect back to the same page.
     * Shopify appends #contact_form to the URL on success.
     * Opens the modal and shows the success state on the submit button.
     *
     * @param {Object} config
     * @param {string} config.modalId    - id of the .modal-overlay element
     * @param {string} config.submitKey  - value stored in sessionStorage to identify which modal
     * @param {string} config.closeFnName - name of the close function on window
     */
    function handleRedirect(config) {
        if (window.location.hash !== '#contact_form') return;

        var key = sessionStorage.getItem('sl_modal_submitted');
        if (key !== config.submitKey) return;

        sessionStorage.removeItem('sl_modal_submitted');

        var modal = document.getElementById(config.modalId);
        if (!modal) return;

        modal.classList.add('active');
        document.body.style.overflow = 'hidden';

        var btn = modal.querySelector('.modal-submit');
        if (btn && window.SardenFormValidator) {
            SardenFormValidator.setButtonSuccess(btn);
            btn.disabled = true;
        }

        setTimeout(function () {
            window[config.closeFnName]();
            if (window.history.replaceState) {
                window.history.replaceState(null, null, window.location.pathname);
            }
        }, 2500);
    }

    /**
     * Wire up a Shopify contact form with validation.
     * Call this inside DOMContentLoaded.
     *
     * @param {Object} config
     * @param {string}  config.formId         - id of the form element
     * @param {string}  config.errorSummaryId - id of the error summary element
     * @param {Array}   config.fields         - SardenFormValidator field config array
     * @param {string}  config.submitKey      - key stored in sessionStorage on submit
     *                                          (used by handleRedirect to re-open correct modal)
     */
    function bindForm(config) {
        var form         = document.getElementById(config.formId);
        var errorSummary = document.getElementById(config.errorSummaryId);

        if (!form || !window.SardenFormValidator) return;

        SardenFormValidator.initForm(form);

        form.addEventListener('submit', function (e) {
            /* Store which modal submitted so handleRedirect can re-open it */
            sessionStorage.setItem('sl_modal_submitted', config.submitKey);

            return SardenFormValidator.handleShopifySubmit(e, {
                form:         form,
                errorSummary: errorSummary,
                fields:       config.fields
            });
        });
    }

    return {
        init:           init,
        handleRedirect: handleRedirect,
        bindForm:       bindForm
    };
})();
